import type { TBrandTokens } from './brand';
import { elevation, SHADOW_ICON_LIGHT } from './elevation';
import { mixOver, type TColorRamp, type TRampStep } from './ramp';

/**
 * Palette factory.
 *
 * Every colour in the system is a *ramp*, and every ramp is exposed whole. A
 * consumer picks the step it wants — `theme.palette.pet[300]` — rather than
 * being limited to whatever `main` / `light` / `text` a factory decided on.
 *
 * Each family is also a valid MUI colour object: MUI's `augmentColor` reads
 * `main` from step 500 and shallow-copies the rest, so the numeric steps
 * survive and `theme.palette.primary.main` keeps working alongside
 * `theme.palette.primary[700]`.
 *
 * `primary` is the Home product. There is no separate brand concept — Home is
 * simply the product a partner leads with, and Secondary / Tertiary / Error
 * carry full ramps exactly like Bird, Trail, Pet and Baby do.
 */

export type TPaletteMode = 'light' | 'dark';

/**
 * A ramp plus named slots.
 *
 * The raw steps stay reachable for one-off needs, but everyday code should
 * use the names: `palette.pet.surface`, not `palette.pet[50]`. If design
 * later decides the product background is step 100, that is one number in
 * DEFAULT_SLOTS rather than an edit in every file that referenced [50].
 */
export type TColorFamily = TColorRamp & {
  /** Step 500 — the colour itself. */
  main: string;
  /** Page / section background tinted with this colour. Step 50. */
  surface: string;
  /** A step deeper than `surface`, for hover and raised fills. */
  surfaceStrong: string;
  /** Lighter and darker companions to `main`. */
  light: string;
  dark: string;
  /** Readable text on a `main` fill. */
  contrastText: string;
  /** This colour's brand gradient. */
  gradient: string;
  /**
   * "Selected" / tinted surfaces — a card that carries this colour without
   * being filled solid with it. In light mode this is just a light ramp
   * step (a tint toward white reads fine on a white page); NOT a raw ramp
   * step in dark mode, because every step is either a tint toward white or
   * a shade toward black — none of them is "dark but still recognisably
   * this colour". Dark composites `main` at low opacity over the card
   * surface instead (see `mixOver` in ramp.ts).
   *
   * Reach for this instead of a raw `family[100]` — that reads fine in
   * light mode and renders as a near-white card with invisible text in
   * dark, which is exactly the bug this field exists to stop.
   */
  tintedSurface: string;
};

/**
 * Which step each derived value reads.
 *
 * Every one is overridable per partner and per mode, so "how light is light"
 * is a decision the design owns, not something baked into this file.
 */
export type TPaletteSteps = {
  /** `.main` on every family. */
  main: TRampStep;
  /** `.light` on every family. */
  light: TRampStep;
  /** `.dark` on every family. */
  dark: TRampStep;
  /** `.surface` — the product-tinted page background. */
  surface: TRampStep;
  /** `.surfaceStrong` — one step deeper than `surface`. */
  surfaceStrong: TRampStep;
  surfaceRaised: TRampStep;
  surfaceSunken: TRampStep;
  surfaceInverse: TRampStep;
  skeleton: TRampStep;
  textDisabled: TRampStep;
  borderDefault: TRampStep;
};

export const DEFAULT_STEPS: Record<TPaletteMode, TPaletteSteps> = {
  light: {
    main: 500,
    light: 400,
    dark: 700,
    // The page tint — `background.default` and `family.surface` — reads
    // step 100, not 50. `surfaceStrong` moves out to 200 with it, so
    // `background.subtle` ("one step deeper than the page tint") stays a
    // distinct step instead of colliding with `default`.
    surface: 100,
    surfaceStrong: 200,
    surfaceRaised: 100,
    surfaceSunken: 200,
    surfaceInverse: 900,
    skeleton: 200,
    textDisabled: 600,
    borderDefault: 700
  },
  dark: {
    main: 500,
    light: 300,
    dark: 700,
    // Page background moved 950 -> 900 on request. surfaceStrong ("one
    // step deeper than the page tint") moves down with it, 900 -> 800, for
    // the same reason the light-mode pair shifted together: left at 900 it
    // would read the exact same step as the new `surface`, colliding with
    // it exactly the way light's surface/surfaceStrong did before that fix.
    surface: 900,
    surfaceStrong: 800,
    surfaceRaised: 900,
    surfaceSunken: 950,
    surfaceInverse: 50,
    skeleton: 900,
    textDisabled: 700,
    borderDefault: 800
  }
};

/**
 * Per-product step overrides — a family that needs a different step than
 * `DEFAULT_STEPS` for one or two slots, without dragging every other
 * product along with it. Keyed by product name, then mode; the family-
 * building loop below does one generic lookup (`{ ...s,
 * ...PRODUCT_STEP_OVERRIDES[name]?.[mode] }`), so adding the next one is a
 * data entry here, not another `name === '…'` branch in shared code.
 *
 * Pet's `surface` (page/tinted-background) is 50 in light mode rather than
 * every other product's 100 — light and dark mirror each other around the
 * ramp's centre (light step `n` <-> dark step `1000 - n`), so dark follows
 * to 950 rather than the shared 900. Every other slot on Pet (`main`,
 * `surfaceStrong`, text, …) stays on `DEFAULT_STEPS`.
 */
const PRODUCT_STEP_OVERRIDES: Record<
  string,
  Partial<Record<TPaletteMode, Partial<TPaletteSteps>>>
> = {
  pet: {
    light: { surface: 50 },
    dark: { surface: 950 }
  }
};

export type TBuildPaletteOptions = {
  /** Override any step. Merged over DEFAULT_STEPS for the mode. */
  steps?: Partial<TPaletteSteps>;
  /**
   * Which product the page background is tinted for. The tint comes from
   * that product's brand ramp (`brand.surfaceRamps`) — 'bird' reads the
   * tertiary ramp, 'pet' the text-tertiary ramp — not the product's own
   * derived ramp. A Bird flow passes 'bird'.
   */
  surfaceFrom?: string;
};

const relativeLuminance = (hex: string): number => {
  const h = hex.replace('#', '');
  const c = [0, 2, 4]
    .map((i) => parseInt(h.slice(i, i + 2), 16) / 255)
    .map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

/**
 * Turns a ramp into a colour family. The whole ramp is spread through, so
 * every step stays reachable.
 *
 * `tintedSurface` is passed in already resolved, not derived from `s` here —
 * light's formula is a plain step lookup, but dark's needs `background.paper`
 * (see the two builders below), which this function has no reason to know
 * about.
 */
const family = (
  ramp: TColorRamp,
  s: TPaletteSteps,
  gradient: string,
  tintedSurface: string,
  surfaceRamp: TColorRamp = ramp
): TColorFamily => ({
  ...ramp,
  main: ramp[s.main],
  // The page tint reads the product's BRAND ramp (brand.surfaceRamps), which
  // for most products is not the ramp of the product colour itself.
  surface: surfaceRamp[s.surface],
  surfaceStrong: surfaceRamp[s.surfaceStrong],
  light: ramp[s.light],
  dark: ramp[s.dark],
  // Readable text on a filled surface of this colour.
  contrastText: relativeLuminance(ramp[s.main]) > 0.5 ? '#000000' : '#FFFFFF',
  gradient,
  tintedSurface
});

/**
 * Everything a mode-specific palette builder needs, plus every field that
 * does NOT differ by mode (products, accents, neutrals, gradients…) already
 * assembled. `buildLightPalette` / `buildDarkPalette` each splice their own
 * `background` / `text` / `border` / `divider` on top — the only things that
 * genuinely read differently per mode.
 *
 * Not exported: this is plumbing for the two builders below, not a public
 * shape callers should depend on.
 */
const buildPaletteCore = (
  brand: TBrandTokens,
  mode: TPaletteMode,
  { steps, surfaceFrom = 'gray' }: TBuildPaletteOptions,
  tintedSurfaceFor: (
    ramp: TColorRamp,
    ctx: { s: TPaletteSteps; r: Record<string, TColorRamp> }
  ) => string
) => {
  const s = { ...DEFAULT_STEPS[mode], ...steps };
  const r = brand.tonal;

  // Every colour family, uniformly — a family with an entry in
  // PRODUCT_STEP_OVERRIDES layers its own steps over the shared ones, but
  // there's no per-product branch here: a family with no entry gets `{
  // ...s, ...undefined }`, i.e. exactly `s`, unchanged.
  const families = Object.fromEntries(
    Object.entries(r).map(([name, ramp]) => {
      const stepsForName = { ...s, ...PRODUCT_STEP_OVERRIDES[name]?.[mode] };
      return [
        name,
        family(
          ramp,
          stepsForName,
          brand.gradients[name] ?? '',
          tintedSurfaceFor(ramp, { s: stepsForName, r }),
          brand.surfaceRamps[name]
        )
      ];
    })
  ) as Record<string, TColorFamily>;

  const surface = brand.surfaceRamps[surfaceFrom] ?? r[surfaceFrom] ?? r.gray;

  return {
    s,
    r,
    surface,
    shared: {
      mode,

      /**
       * `primary` is not a colour of its own — it is a pointer to whichever
       * product the current page is themed to, and defaults to Home.
       *
       * It exists because MUI's own components (Button, Checkbox, Radio,
       * Link) read `palette.primary`; withProduct() re-points it, which is
       * what makes a Pet page's buttons and controls turn pink without
       * touching markup.
       */
      primary: families[surfaceFrom] ?? families.home,
      home: families.home,

      // Products
      bird: families.bird,
      trail: families.trail,
      pet: families.pet,
      baby: families.baby,

      // Accents — full ramps, exactly like the products
      secondary: families.secondary,
      tertiary: families.tertiary,
      error: families.error,
      /** Warning shares the secondary accent, per the colour mapping. */
      warning: families.secondary,

      // Neutrals
      gray: families.gray,
      black: families.black,

      common: { white: '#FFFFFF', black: r.black[500] },

      /**
       * Brand gradients per colour.
       *
       * `brand` is the active product's gradient under a stable name, so a
       * component can say "the current product's gradient" without naming
       * the product. withProduct() re-points it; this default keeps it
       * defined even when no product provider is mounted.
       */
      gradient: {
        ...brand.gradients,
        brand: brand.gradients[surfaceFrom] ?? brand.gradients.home
      },
      // `icon` is the one shadow that isn't mode-invariant — see
      // SHADOW_ICON_LIGHT's own comment.
      shadows: {
        ...elevation,
        icon: mode === 'light' ? SHADOW_ICON_LIGHT : elevation.icon
      },

      /** Raw ramps, without the MUI contract mixed in. */
      tonal: r
    }
  };
};

/**
 * Light-mode palette. Cards and sheets are literal white — that's the Glass
 * spec, not a ramp step — and text/divider come straight from what the
 * partner declared (`brand.text`, `brand.divider`), not the neutral ramp.
 */
export const buildLightPalette = (
  brand: TBrandTokens,
  options: TBuildPaletteOptions
) => {
  // A light ramp step tints toward white, which reads fine as a "selected"
  // surface — but not at step 100: the page itself is tinted at step 100
  // (DEFAULT_STEPS.light.surface), so a step-100 "selected" surface is the
  // exact same colour as the page behind it, distinguishable only by its
  // border. Reported live on the
  // plan-comparison selected card, which was otherwise invisible against
  // the page. Step 200 is a deeper tint — still light, still white-adjacent,
  // but a full step past whatever the page itself is tinted to.
  const { s, r, surface, shared } = buildPaletteCore(
    brand,
    'light',
    options,
    (ramp) => ramp[200]
  );

  return {
    ...shared,

    /**
     * Backgrounds: the page is tinted with the active product (step 100 —
     * see DEFAULT_STEPS.light.surface); cards and sheets sit white above it.
     */
    background: {
      /** Product-tinted page ground. */
      default: surface[s.surface],
      /** One step deeper, for sections that need separation from the page. */
      subtle: surface[s.surfaceStrong],
      paper: '#FFFFFF',
      card: '#FFFFFF',
      light: '#FFFFFF',
      dark: r.gray[s.surfaceSunken],
      opposite: r.gray[s.surfaceInverse],
      skeleton: r.gray[s.skeleton]
    },

    text: {
      primary: brand.text.primary,
      secondary: brand.text.secondary,
      tertiary: brand.text.tertiary,
      contrastText: '#FFFFFF',
      disabled: r.gray[s.textDisabled]
    },

    border: {
      default: r.gray[s.borderDefault],
      divider: brand.divider
    },
    divider: brand.divider
  };
};

/**
 * Dark-mode palette. Cards and sheets read the neutral ramp instead of a
 * literal white, and text/divider mirror up that same ramp rather than the
 * partner's light-mode-authored values.
 */
export const buildDarkPalette = (
  brand: TBrandTokens,
  options: TBuildPaletteOptions
) => {
  // No ramp step is both dark and recognisably this colour — every step
  // tints toward white or shades toward black. Composite a SHADE of the
  // colour (step 700, already dark) at low opacity over the same neutral
  // the cards use, so a "selected" surface reads as a tinted dark card, not
  // another near-black step.
  //
  // This used to composite `main` (step 500 — the bright, undiluted colour)
  // instead of a shade, which pulled the result up toward that brightness
  // and left it reading too close to the page itself; step 700 keeps the
  // result recognisably tinted without cancelling out the "darker than a
  // plain card" cue a selected surface needs.
  const { s, r, surface, shared } = buildPaletteCore(
    brand,
    'dark',
    options,
    (ramp, { s: steps, r: ramps }) =>
      mixOver(ramps.gray[steps.surfaceRaised], ramp[700], 0.3)
  );

  return {
    ...shared,

    /**
     * Backgrounds: the page is tinted with the active product (step 950 —
     * see DEFAULT_STEPS.dark.surface); cards and sheets sit one neutral step
     * above it, not white.
     */
    background: {
      /** Product-tinted page ground. */
      default: surface[s.surface],
      /** One step deeper, for sections that need separation from the page. */
      subtle: surface[s.surfaceStrong],
      paper: r.gray[s.surfaceRaised],
      card: r.gray[s.surfaceRaised],
      light: r.gray[s.surfaceRaised],
      dark: r.gray[s.surfaceSunken],
      opposite: r.gray[s.surfaceInverse],
      skeleton: r.gray[s.skeleton]
    },

    // Partner-authored, same as light — was ramp-derived (plus a literal
    // '#FFFFFF' override for `primary`), which silently ignored whatever a
    // partner's own DARK_SEED.text actually said. `disabled` stays
    // ramp-derived in both modes: it was never part of TBrandSeed.text.
    text: {
      primary: brand.text.primary,
      secondary: brand.text.secondary,
      tertiary: brand.text.tertiary,
      contrastText: '#FFFFFF',
      disabled: r.gray[s.textDisabled]
    },

    border: {
      default: r.gray[s.borderDefault],
      // Not a ramp step: `surfaceRaised` (background.paper) and a neutral
      // shade both come from the SAME gray ramp, so any flat step chosen
      // for the divider risks landing on the exact colour of the card it's
      // meant to separate — which is exactly what happened (both read step
      // 900). A translucent white overlay stays visibly distinct from
      // whatever it sits on, card or page, without needing to know which.
      divider: 'rgba(255, 255, 255, 0.16)'
    },
    divider: 'rgba(255, 255, 255, 0.16)'
  };
};

/**
 * Picks the right builder for `mode`. Prefer calling `buildLightPalette` /
 * `buildDarkPalette` directly when the mode is known statically (every
 * partner's `lightTheme.ts` / `darkTheme.ts` does); this dispatcher exists
 * for the handful of callers — tests, anything reading `theme.palette.mode`
 * at runtime — that only know the mode as a variable.
 */
export const buildPalette = (
  brand: TBrandTokens,
  mode: TPaletteMode,
  options: TBuildPaletteOptions
) =>
  mode === 'light'
    ? buildLightPalette(brand, options)
    : buildDarkPalette(brand, options);
